import { useEffect, useState } from 'react';

import {
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  Password,
  rem,
  VStack,
} from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

const MIN_CHAR_PASSWORD = 1;
const MAX_CHAR_PASSWORD = 32;

export interface PasswordFormProps {
  /** Whether the password form will show "isInvalid" status */
  isInvalid: boolean;
  minLength?: number;
  maxLength?: number;
  /** If error, password from will show error message under the input */
  error?: Error | null;
  /** Fire when password form submit */
  onSubmit: (password: string) => void;
}

export const PasswordForm = ({
  isInvalid,
  minLength = MIN_CHAR_PASSWORD,
  maxLength = MAX_CHAR_PASSWORD,
  children,
  error,
  onSubmit,
}: React.PropsWithChildren<PasswordFormProps>) => {
  const [password, setPassword] = useState('');
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(isInvalid);
  }, [isInvalid]);

  const handleSubmit = () => {
    onSubmit?.(password);
  };

  return (
    <Center m="100">
      <VStack gap={{ base: '50', md: '100' }}>
        {children}

        <Flex gap="100" direction="column" w="100%" maxW={rem(448)}>
          <FormControl display="flex" flexDirection="column" isInvalid={hasError}>
            <Password
              minLength={minLength}
              maxLength={maxLength}
              placeholder={translate('Enter password')}
              aria-required="true"
              autoComplete="none"
              data-lpignore="true"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPassword(e.target.value);
                hasError && setHasError(false);
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              w="100%"
              // @ts-expect-error Permit resizing password of input field
              size={{ base: 'sm', md: 'md' }}
            />
            <FormErrorMessage>{error?.message}</FormErrorMessage>
          </FormControl>
          <Button
            type="submit"
            size={{ base: 'sm', md: 'md' }}
            onClick={handleSubmit}
            isDisabled={hasError || !password}
          >
            {translate('Submit')}
          </Button>
        </Flex>
      </VStack>
    </Center>
  );
};
